import React, { useMemo, useEffect, useState } from 'react';
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode } from '@lexical/rich-text';
import { LinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkNode } from '@lexical/link';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import CustomHistoryActions from '../EditorPlugIns/CustomHistoryActions';
import OnChangePlugin from '../EditorPlugIns/OnChangePlugin/OnChangePlugin';
import CustomHeadingActions from '../EditorPlugIns/CustomHeadingActions/CustomHeadingActions';
import CustomHeadingPlugin from '../EditorPlugIns/CustomHeadingPlugin/CustomHeadingPlugin';
import './TextEditor.css';
import ToolbarPlugin from '../EditorPlugIns/CustomImagesPlugIn/ImageToolbar';
import ImagesPlugin from '../EditorPlugIns/CustomImagesPlugIn/ImagesPlugin';
import { ImageNode } from '../EditorPlugIns/nodes/ImageNode';
import { fsParagraphsToEditorState } from '../../utils/editor';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

interface TextEditorProps {
  initialData: any;
  withDescription?: boolean;
  getDatas: (data: object) => void;
  placeholder?: string;
}

const TextEditor: React.FC<TextEditorProps> = ({
  initialData,
  withDescription = true,
  getDatas,
  placeholder = "Add a sale summary"
}) => {
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  // Handler for editor data changes
  const handleData = (data: any) => {
    if (data && data.root && data.root.children && data.root.children.length > 0) {
      // Check if we have actual content, not just empty paragraphs
      const hasRealContent = data.root.children.some((child: any) => {
        if (child.children && child.children.length > 0) {
          return child.children.some((textNode: any) => 
            textNode.text && textNode.text.trim() !== '' || 
            textNode.type === 'image'
          );
        }
        return false;
      });
      
      setHasContent(hasRealContent);
    } else {
      setHasContent(false);
    }
    
    getDatas(data);
  };

  // Prepare initial editor state
  const initialText = useMemo(() => {
    if (Array.isArray(initialData) && initialData.length > 0) {
      const editorState = fsParagraphsToEditorState(initialData);
      setHasContent(true);
      return JSON.stringify(editorState);
    } 
    return undefined;
  }, [initialData]);

  // Editor configuration
  const lexicalConfig: InitialConfigType = {
    namespace: 'Equitee Fund Editor',
    nodes: [HeadingNode, LinkNode, AutoLinkNode, ImageNode],
    theme: {
      text: {
        bold: 'text-bold',
        italic: 'text-italic',
        underline: 'text-underline',
        code: 'text-code',
        highlight: 'text-highlight',
        strikethrough: 'text-strikethrough',
        subscript: 'text-subscript',
        superscript: 'text-superscript',
      },
      heading: {
        h1: 'text-5xl font-extrabold text-black',
        h2: 'text-4xl font-bold text-black',
        h3: 'text-3xl font-bold text-black',
        h4: 'text-2xl font-bold text-black',
        h5: 'text-xl font-bold text-black',
      },
    },
    editorState: initialText,
    onError: (e) => {
      console.error("Lexical Editor Error:", e);
    },
  };

  // Custom placeholder component
  const CustomPlaceholder = useMemo(() => {
    // Only show placeholder if there's no content
    if (hasContent) return null;
    
    return (
      <>
        <p className="absolute text-[#AAAAAA] opacity-50 top-[48px] left-1 z-50 p-5 text-sm pointer-events-none">
          {placeholder}
        </p>
        {withDescription && (
          <p className="absolute text-[#AAAAAA] opacity-50 bottom-0 z-50 p-5 text-sm w-full whitespace-normal">
            There are 3 types of paragraphs you can add - text paragraph with a
            title, text paragraph without a title, image paragraph
          </p>
        )}
      </>
    );
  }, [withDescription, hasContent, placeholder]);

  return (
    <div className="relative mt-1.5 rounded-md border border-[#CBD5E1]">
      <LexicalComposer initialConfig={lexicalConfig}>
        <div className="sticky rounded-md z-10 w-full flex items-center gap-2 bg-transparent p-[10px]">
          <CustomHistoryActions />
          <CustomHeadingActions />
          <ToolbarPlugin />
        </div>
        <hr className="mb-4 border border-solid border-slate-200 mx-3" />
        <RichTextPlugin
          contentEditable={
            <ContentEditable 
              className="w-full h-[352px] flex-1 px-5 bg-transparent outline-none overflow-y-scroll editor-scrollable" 
            />
          }
          placeholder={CustomPlaceholder}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <CustomHeadingPlugin />
        <ImagesPlugin />
        <LinkPlugin />
        <OnChangePlugin getData={handleData} />
        <InitialDataPlugin 
          initialData={initialData} 
          onInitialized={() => setEditorInitialized(true)} 
        />
      </LexicalComposer>
    </div>
  );
};

// Enhanced InitialDataPlugin with better initialization logic
const InitialDataPlugin: React.FC<{ 
  initialData: any, 
  onInitialized: () => void 
}> = ({ initialData, onInitialized }) => {
  const [editor] = useLexicalComposerContext();
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (Array.isArray(initialData) && initialData.length > 0 && !hasInitialized) {
      try {
        const editorStateJSON = JSON.stringify(
          fsParagraphsToEditorState(initialData)
        );
        const parsedEditorState = editor.parseEditorState(editorStateJSON);
        editor.setEditorState(parsedEditorState);
        setHasInitialized(true);
        onInitialized();
      } catch (error) {
        console.error("Error initializing editor with data:", error);
        // Create a default state with just the text if parsing fails
        if (initialData[0] && initialData[0].text) {
          editor.update(() => {
            const root = editor.getRootElement();
            if (root) {
              root.textContent = initialData[0].text;
            }
          });
        }
        setHasInitialized(true);
        onInitialized();
      }
    } else if (!hasInitialized) {
      setHasInitialized(true);
      onInitialized();
    }
  }, [initialData, editor, hasInitialized, onInitialized]);

  return null;
};

export default TextEditor;